import React from 'react';
import { Map as MapIcon, ListTree } from 'lucide-react';
import { Button } from '@repo/ui';

export type ViewMode = 'map' | 'list';

export interface ViewToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}

/**
 * Map / List mode switch for the Scans tab.
 *
 * Sits INLINE in the search row beside Filter and Sort (VERA-476), sized and styled
 * identically to them so the row reads as three equal controls.
 *
 * History, so this isn't re-litigated: it began as its own full-width row (three rows
 * of chrome above the content on a 375px screen), then as a floating pill — which was
 * wrong, because that pattern belongs to full-screen map apps where the map IS the
 * viewport, whereas here the map is a card in a scrolling document, so the pill had
 * nothing to float over and collided with the page's tab row.
 *
 * A two-segment control was then tried and measured out: the row is 343px, and
 * Filter (48) + Sort (48) + a 2-segment track (104) + gaps (16) = 216px left the search
 * field only 115px. Shrinking the segments enough to fit would have put them under the
 * 44px minimum touch target. Hence a single button.
 *
 * A one-icon toggle is ambiguous unless you fix a convention, so: the icon shows the
 * mode you will GET, not the one you are in (the same convention as Airbnb's "Show
 * map"). The tooltip and aria-label say so explicitly, since the icon alone can't.
 */
export const ViewToggle: React.FC<ViewToggleProps> = ({ value, onChange }) => {
  const goingToMap = value === 'list';
  const label = goingToMap ? 'Show map' : 'Show list';

  return (
    // Native `title` rather than a MUI Tooltip: @repo/ui's Button is a plain function
    // component, so Tooltip's ref forwarding fails on it ("Function components cannot
    // be given refs"). `title` also matches the existing kebab button in this same bar.
    <Button
      variant='tertiary'
      size='large'
      aria-label={label}
      title={label}
      onClick={() => onChange(goingToMap ? 'map' : 'list')}
      sx={{ padding: '12px', paddingX: '12px' }}
    >
      {goingToMap ? <MapIcon size={22} /> : <ListTree size={22} />}
    </Button>
  );
};

export default ViewToggle;
